import SearchIcon from '@mui/icons-material/Search';
import { Card, CircularProgress, Grid, InputBase, Stack, styled } from '@mui/material';
import { DataGrid, GridColDef, GridRowId } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { FC, useEffect, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { getAllActiveInformationCategory } from 'wisestep-api';
import { WsButton, WsDrawer } from 'wisestep-base';

export interface IInformationTemplateDetails {
  id: string;
  categoryName: string;
  createdBy: string;
  category: {
    id: string;
    templateName: string;
    createdBy: string;
    title: string;
  };
  rtrTemplate: string | null;
}

const StyledInputBase = styled(InputBase)(() => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    width: '100%',
  },
}));

const SearchIconWrapper: FC = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 1),
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export interface IComponentProps {
  open: boolean;
  onClose: () => void;
  setSelectedTemplates: (
    infoTemplates: IInformationTemplateDetails[],
    hasRtrTemplates: boolean
  ) => void;
  selectedTemplates: IInformationTemplateDetails[];
}

const SelectInformationTemplateDrawer = ({
  open,
  onClose,
  setSelectedTemplates,
  selectedTemplates,
}: IComponentProps) => {
  const methods = useForm();
  const { handleSubmit } = methods;
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>(selectedTemplates.map((e) => e.id));
  const [templateData, setTemplateData] = useState<IInformationTemplateDetails[]>([]);
  const [originalTemplateData, setOriginalTemplateData] = useState<IInformationTemplateDetails[]>(
    []
  );
  const [tableData, setTableData] = useState<Record<string, unknown>[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveInformationCategory();
      if (response.data) {
        setTemplateData(response.data);
        setOriginalTemplateData(response.data);
      }
    })();
  }, []);

  useEffect(() => {
    if (templateData) {
      const newData = templateData.map((item) => ({
        id: item.id,
        templateName: !isEmpty(item.categoryName) ? item.categoryName : '_missing_',
        isRTR: item.rtrTemplate ? 'Yes' : 'No',
      }));
      setTableData(newData);
    }
  }, [templateData]);

  useEffect(() => {
    const setPreSelectedRows = () => {
      if (selectedTemplates.length > 0) {
        const selectedIds: GridRowId[] = [];
        selectedTemplates.forEach((element) => {
          selectedIds.push(element.id);
        });
        setSelectedRows(selectedIds);
      }
    };
    setPreSelectedRows();
  }, [templateData]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'id', hide: true },
    { field: 'templateName', headerName: 'Information Category', flex: 4 },
    { field: 'isRTR', headerName: 'RTR', flex: 4 },
  ];

  const onSearch = (query) => {
    if (query) {
      const filteredData = originalTemplateData.filter((item) =>
        item.categoryName.toLowerCase().includes(query.toLowerCase())
      );
      setTemplateData(filteredData);
    } else {
      setTemplateData(originalTemplateData);
    }
  };

  const fetchSelectedTemplates = () => {
    setIsLoading(true);
    const selectedTemplates = templateData.filter((template) => selectedRows.includes(template.id));
    const rtrTemplates = selectedTemplates.filter((template) => template.rtrTemplate);
    setSelectedTemplates(selectedTemplates, rtrTemplates.length > 0);
    onClose();
    setIsLoading(false);
  };

  return (
    <WsDrawer open={open} onClose={onClose} title="Select Information Template" size="small">
      <FormProvider {...methods}>
        <Grid
          container
          component="form"
          onSubmit={handleSubmit(onSearch as SubmitHandler<FieldValues>)}
        >
          <Card sx={{ width: 903, p: 1 }}>
            <Stack direction="row">
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                id="search-input"
                placeholder="Search Information Templates"
                onChange={(event) => onSearch(event.target.value)}
                fullWidth
              />
            </Stack>
          </Card>
        </Grid>
        <Grid container item xs={12} pt={1.5} pl={1.5}>
          <div style={{ height: 718, width: 903 }}>
            <DataGrid
              columns={columns}
              rows={tableData}
              loading={tableData.length === 0}
              checkboxSelection
              onSelectionModelChange={(newSelectionModel) => setSelectedRows(newSelectionModel)}
              selectionModel={selectedRows}
            />
          </div>
        </Grid>
        <Grid container item justifyContent="space-between" xs={12} p={1.5}>
          <WsButton variant="text">{`${selectedRows?.length ?? 0} Selected`}</WsButton>
          <WsButton
            variant="contained"
            size="large"
            onClick={fetchSelectedTemplates}
            disabled={isLoading}
            endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : undefined}
          >
            {isLoading ? 'Saving...' : 'Done'}
          </WsButton>
        </Grid>
      </FormProvider>
    </WsDrawer>
  );
};
export default SelectInformationTemplateDrawer;
